import React, { useState, useEffect } from 'react';
import { Box, Button, IconButton, Modal, Typography } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import modalStyle from "../../styles/modalStyle";
import { FieldAlias, FieldAliasRequest } from "../../interfaces/field";
import Field from "../../interfaces/field";
import apiClient from "../../axiosClient";
import { useSelector } from "react-redux";
import { RootState } from "../../store/store";
import store from "../../store/store";
import { decodedJWT, getOwnerName } from "../../utils/commonUtils";
import { processExcelDataWithValidation } from "../../utils/uploadValidationUtils";
import { v4 as uuidv4 } from 'uuid';
import MandatoryFieldRow from '../manageFields/components/MandatoryFieldRow';
import CustomFieldRow from '../manageFields/components/CustomFieldRow';
import CustomerReferenceFieldRow from '../manageFields/components/CustomerReferenceFieldRow';
import { FieldAliasRow } from '../manageFields/types/fieldAliasTypes';


interface ConfigureFieldAliasModalProps {
    open: boolean;
    onClose: () => void;
    initialFieldAliases: FieldAlias[]; // Pass existing field aliases
    loanType: string; // Current loan type being configured
    onSave: () => void; // Callback to refresh parent data
}

const ConfigureFieldAliasModal: React.FC<ConfigureFieldAliasModalProps> = ({
    open,
    onClose,
    initialFieldAliases,
    loanType,
    onSave: onSaveCallback
}) => {
    const [fieldAliasRows, setFieldAliasRows] = useState<FieldAliasRow[]>([]);
    const allFields = useSelector((state: RootState) => state.fields.fields);
    const token = decodedJWT();
    const client_name = getOwnerName(token);

    const onModalClose = () => {
        setFieldAliasRows([]);
        onClose();
    };
    // Get all mandatory standard fields that must have aliases (PRIMARY, SUB_PRIMARY, and SECONDARY)
    const mandatoryStandardFields = allFields.filter((field: Field) => 
        field.is_standard && ['PRIMARY', 'SUB_PRIMARY', 'SECONDARY'].includes(field.field_type)
    );
    
    // Sort by field type priority: PRIMARY first, then SUB_PRIMARY, then SECONDARY
    const sortedMandatoryFields = mandatoryStandardFields.sort((a, b) => {
        const priority = { 'PRIMARY': 1, 'SUB_PRIMARY': 2, 'SECONDARY': 3 };
        return priority[a.field_type as keyof typeof priority] - priority[b.field_type as keyof typeof priority];
    });
    
    const customerDetailFields = allFields.filter((field: Field) => 
        field.field_type === 'CUSTOMER_DETAIL'
    );
    
    const customerDetailAttributeFields = allFields.filter((field: Field) => 
        field.field_type === 'CUSTOMER_DETAIL_ATTRIBUTE'
    );
    
    // Filter secondary, tertiary fields by client_name to show existing secondary, tertiary fields for the client
    const secondaryFields = allFields.filter((field: Field) => 
        ( field.field_type === 'SECONDARY' || field.field_type === 'TERTIARY' ) && field.client_name === client_name
    );

    // Helper functions for alias name validation
    const checkForDuplicateAliases = (rows: FieldAliasRow[]): string[] => {
        const aliasCount: Record<string, number> = {};
        const duplicates: string[] = [];
        
        rows.forEach(row => {
            if (row.is_active) { // Only check active rows
                const trimmedAlias = row.alias_name.trim();
                if (trimmedAlias) {
                    aliasCount[trimmedAlias] = (aliasCount[trimmedAlias] || 0) + 1;
                    if (aliasCount[trimmedAlias] > 1 && !duplicates.includes(trimmedAlias)) {
                        duplicates.push(trimmedAlias);
                    }
                }
            }
        });
        
        return duplicates;
    };

    const hasDuplicateAliases = (rows: FieldAliasRow[]): boolean => {
        return checkForDuplicateAliases(rows).length > 0;
    };

    const isDuplicateAliasName = (aliasName: string, rows: FieldAliasRow[], currentId: string): boolean => {
        const trimmedAlias = aliasName.trim();
        return rows.some(row => 
            row.id !== currentId && 
            row.is_active && 
            row.alias_name.trim() === trimmedAlias
        );
    };

    // Create wrapper function for components that matches expected signature
    const isDuplicateAliasNameForComponent = (aliasName: string, currentId: string): boolean => {
        return isDuplicateAliasName(aliasName, fieldAliasRows, currentId);
    };

    // Load data when modal opens
    useEffect(() => {
        if (open) {
            loadFieldAliasRows();
        }
    }, [open, initialFieldAliases, allFields]);


    const createEmptyRow = (): FieldAliasRow => ({
        id: uuidv4(),
        field_alias_id: '',
        customer_detail_field_id: null,
        field_id: null,
        field_name: '',
        alias_name: '',
        field_label: '',
        client_name: client_name || '',
        loan_type: loanType,
        field_description: '',
        field_value_type: 'Text',
        field_type: 'SECONDARY',
        is_standard: false,
        is_active: true,
        updated_date: '',
        updated_by: '',
        created_date: '',
        created_by: '',
        customerDetailType: '',
        customerDetailNumber: undefined,
        customerDetailAttribute: '',
        isMandatoryStandard: false,
        isSubPrimary: false,
    });

    const createMandatoryStandardFieldRow = (standardField: Field): FieldAliasRow => ({
        id: uuidv4(),
        field_alias_id: '',
        customer_detail_field_id: null,
        field_id: standardField.field_id,
        field_name: standardField.field_name,
        alias_name: standardField.field_name, // Default alias same as field name
        field_label: standardField.field_label || standardField.field_name,
        client_name: client_name || '',
        loan_type: loanType,
        field_description: standardField.field_description || '',
        field_value_type: standardField.field_value_type,
        field_type: standardField.field_type,
        is_standard: true,
        is_active: true,
        updated_date: '',
        updated_by: '',
        created_date: '',
        created_by: '',
        customerDetailType: '',
        customerDetailNumber: undefined,
        customerDetailAttribute: '',
        isMandatoryStandard: true,
        isSubPrimary: standardField.field_type === 'SUB_PRIMARY',
    });

    const loadFieldAliasRows = () => {
        // Preserve existing user-created rows that aren't saved yet
        const existingUserRows = fieldAliasRows.filter(row => 
            !row.field_alias_id && !row.is_standard // newly added field_alias which yet does not exist in the db  
        );
        
        const rows: FieldAliasRow[] = [];
        
        // 1. First, add mandatory standard fields (PRIMARY, SECONDARY)
        sortedMandatoryFields.forEach(standardField => {
            const existingAlias = initialFieldAliases.find(alias => alias.field_id === standardField.field_id);
            
            if (existingAlias) {
                // Load existing standard field alias
                const row: FieldAliasRow = {
                    ...existingAlias,
                    id: existingAlias.field_alias_id,
                    loan_type: loanType, // Ensure loan_type is set
                    customerDetailType: '',
                    customerDetailNumber: undefined,
                    customerDetailAttribute: '',
                    isMandatoryStandard: true,
                    isSubPrimary: standardField.field_type === 'SUB_PRIMARY',
                };
                rows.push(row);
            } else {
                // Create new row for standard field that doesn't have an alias yet
                rows.push(createMandatoryStandardFieldRow(standardField));
            }
        });
        
        // 2. Then, add existing non-mandatory standard field aliases
        const nonMandatoryAliases = initialFieldAliases.filter(alias => {
            const matchingField = allFields.find(field => field.field_id === alias.field_id);
            return !matchingField || 
                   !matchingField.is_standard || 
                   !['PRIMARY', 'SUB_PRIMARY', 'SECONDARY'].includes(matchingField.field_type);
        });
        
        nonMandatoryAliases.forEach(alias => {
            const row: FieldAliasRow = {
                ...alias,
                id: alias.field_alias_id,
                loan_type: loanType, // Ensure loan_type is set
                customerDetailType: '',
                customerDetailNumber: undefined,
                customerDetailAttribute: '',
                isMandatoryStandard: false,
                isSubPrimary: false,
            };

            // If it's a customer reference field, populate the UI state
            if (alias.customer_detail_field_id) {
                const customerDetailField = customerDetailFields.find(field => 
                    field.field_id === alias.customer_detail_field_id
                );
                if (customerDetailField) {
                    row.customerDetailType = customerDetailField.field_name;
                }

                const customerDetailAttributeField = customerDetailAttributeFields.find(field => 
                    field.field_id === alias.field_id
                );
                if (customerDetailAttributeField) {
                    row.customerDetailAttribute = customerDetailAttributeField.field_name;
                }

                if (row.customerDetailType && row.customerDetailType !== 'customer' && alias.field_name) {
                    const match = alias.field_name.match(/_(\d+)_/);
                    if (match) {
                        row.customerDetailNumber = parseInt(match[1]);
                    }
                }
            }

            rows.push(row);
        });
        
        // 3. Add preserved user-created rows
        existingUserRows.forEach(userRow => {
            rows.push(userRow);
        });
        
        // 4. If no rows exist, add one empty row this will never happen
        if (rows.length === 0) {
            rows.push(createEmptyRow());
        }
        
        
        setFieldAliasRows(rows);
    };

    const updateRow = (id: string, field: keyof FieldAliasRow, value: any) => {
        
        setFieldAliasRows(prev => prev.map(row =>
            row.id === id ? { ...row, [field]: value } : row
        ));
    };

    const addNewRow = () => {
        const newRow = createEmptyRow();
        setFieldAliasRows([...fieldAliasRows, newRow]);
    };

    const removeRow = (id: string) => {
        const row = fieldAliasRows.find(r => r.id === id);
        
        // Prevent deletion of mandatory standard fields
        if (row && row.isMandatoryStandard) {
            alert('Cannot delete mandatory field aliases. You can only modify the alias name.');
            return;
        }
        
        // For new rows (no field_alias_id), actually remove from array
        if (row && !row.field_alias_id) {
            setFieldAliasRows(fieldAliasRows.filter(row => row.id !== id));
        } else {
            // For existing rows (with field_alias_id), mark as inactive for soft delete
            updateRow(id, 'is_active', false);
        }
    };

    const undoDelete = (id: string) => {
        updateRow(id, 'is_active', true);
    };


    const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        try {
            // Process Excel file with proper validation (requires Sheet1)
            const validation = await processExcelDataWithValidation(file);
            
            if (!validation.isValid) {
                alert(validation.errors.join('\n'));
                return;
            }

            // Extract headers from validated data and clean them
            const rawHeaders = validation.data?.[0] || [];
            const headers = rawHeaders
                .map(header => header != null ? String(header).trim() : '')
                .filter(header => header !== '');

            // Get existing alias names to compare
            const existingAliasNames = fieldAliasRows
                .filter(row => row.is_active)
                .map(row => row.alias_name);

            // Find headers that don't exist in current field aliases
            const newHeaders = headers.filter(header => 
                header && 
                header !== '' && 
                !existingAliasNames.includes(header)
            );

            if (newHeaders.length > 0) {
                // Create new field alias rows for non-existing headers
                const newRows = newHeaders.map(header => {
                    const newRow = createEmptyRow();
                    return {
                        ...newRow,
                        alias_name: header,
                        field_label: header.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
                    };
                });

                // Add new rows to existing ones
                setFieldAliasRows(prev => [...prev, ...newRows]);
                
                alert(`Added ${newHeaders.length} new field aliases from Excel headers: ${newHeaders.join(', ')}`);
            } else {
                alert('No new headers found. All headers already exist as field aliases.');
            }

        } catch (error: any) {
            console.error('Error processing Excel file:', error);
            alert(error.message || 'Error processing Excel file. Please ensure it\'s a valid Excel file with a worksheet named "Sheet1".');
        }
        
        // Clear the input value to allow uploading the same file again
        event.target.value = '';
    };

    // Helper function to generate customer reference field names
    const generateCustomerReferenceFieldName = (
        customerDetailType: string, 
        customerDetailAttribute: string, 
        customerDetailNumber?: number
    ): string => {
        if (customerDetailType === 'customer') {
            return `customer_${customerDetailAttribute}`;
        } else {
            return `${customerDetailType}_${customerDetailNumber}_${customerDetailAttribute}`;
        }
    };

    // Customer detail numbers (1-4)
    const getCustomerDetailNumbers = (): string[] => {
        return ['1', '2', '3', '4'];
    };

    const handleFieldSelection = (rowId: string, selectedFieldId: string) => {
        if (selectedFieldId === 'CUSTOMER_REFERENCE') {
            // Customer reference field selected
            updateRow(rowId, 'field_id', null);
            updateRow(rowId, 'field_name', '');
            updateRow(rowId, 'field_label', '');
            updateRow(rowId, 'field_description', '');
            updateRow(rowId, 'field_value_type', 'Text');
            updateRow(rowId, 'field_type', 'CUSTOMER_DETAIL_ATTRIBUTE');
            updateRow(rowId, 'is_standard', true); // Customer references are standard
            updateRow(rowId, 'customer_detail_field_id', ''); // Initialize to show customer reference UI
            updateRow(rowId, 'customerDetailType', '');
            updateRow(rowId, 'customerDetailNumber', undefined);
            updateRow(rowId, 'customerDetailAttribute', '');
        } else {
            // Secondary field selected from existing fields
            // First try to find in secondaryFields, then in allFields (for newly created fields)
            let selectedField = secondaryFields.find(field => field.field_id === selectedFieldId);
            
            if (!selectedField) {
                selectedField = allFields.find(field => field.field_id === selectedFieldId);
                
                // If still not found, try the fresh store state (for newly created fields)
                if (!selectedField) {
                    const freshAllFields = store.getState().fields.fields;
                    selectedField = freshAllFields.find(field => field.field_id === selectedFieldId);
                }
            }

            if (selectedField) {
                
                updateRow(rowId, 'field_id', selectedField.field_id);
                updateRow(rowId, 'field_name', selectedField.field_name);
                updateRow(rowId, 'field_label', selectedField.field_label);
                updateRow(rowId, 'field_description', selectedField.field_description);
                updateRow(rowId, 'field_value_type', selectedField.field_value_type);
                updateRow(rowId, 'field_type', selectedField.field_type);
                updateRow(rowId, 'is_standard', selectedField.is_standard);
                updateRow(rowId, 'customer_detail_field_id', undefined); // REMOVE customer reference UI
                updateRow(rowId, 'customerDetailType', undefined);
                updateRow(rowId, 'customerDetailNumber', undefined);
                updateRow(rowId, 'customerDetailAttribute', undefined);
            }
        }
    };

    const handleCustomerDetailTypeSelection = (rowId: string, selectedType: string) => {
        updateRow(rowId, 'customerDetailType', selectedType);
        
        // Find the CUSTOMER_DETAIL field for this type and set customer_detail_field_id
        const customerDetailField = customerDetailFields.find(field => 
            field.field_name === selectedType
        );
        
        if (customerDetailField) {
            updateRow(rowId, 'customer_detail_field_id', customerDetailField.field_id);
        }
        
        if (selectedType === 'customer') {
            updateRow(rowId, 'customerDetailNumber', undefined);
        } else {
            updateRow(rowId, 'customerDetailNumber', 1); // Default to 1
        }
        updateRow(rowId, 'customerDetailAttribute', '');
        updateRow(rowId, 'field_id', null); // Reset field_id when type changes
        updateRow(rowId, 'field_name', '');
    };

    const handleCustomerDetailNumberSelection = (rowId: string, selectedNumber: number) => {
        updateRow(rowId, 'customerDetailNumber', selectedNumber);
        const row = fieldAliasRows.find(r => r.id === rowId);
        if (row && row.customerDetailType && row.customerDetailAttribute) {
            const fieldName = generateCustomerReferenceFieldName(
                row.customerDetailType, 
                row.customerDetailAttribute, 
                selectedNumber
            );
            updateRow(rowId, 'field_name', fieldName);
        }
    };

    const handleCustomerDetailAttributeSelection = (rowId: string, selectedAttribute: string) => {
        updateRow(rowId, 'customerDetailAttribute', selectedAttribute);
        
        // Find the CUSTOMER_DETAIL_ATTRIBUTE field for this attribute and set field_id
        const customerDetailAttributeField = customerDetailAttributeFields.find(field => 
            field.field_name === selectedAttribute
        );
        
        if (customerDetailAttributeField) {
            updateRow(rowId, 'field_id', customerDetailAttributeField.field_id);
        }
        
        const row = fieldAliasRows.find(r => r.id === rowId);
        if (row && row.customerDetailType) {
            const fieldName = generateCustomerReferenceFieldName(
                row.customerDetailType, 
                selectedAttribute, 
                row.customerDetailNumber
            );
            updateRow(rowId, 'field_name', fieldName);
            updateRow(rowId, 'field_label', fieldName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()));
        }
    };

    const saveFieldAliases = async () => {
        try {
            // Check for duplicate alias names before saving
            const duplicateAliases = checkForDuplicateAliases(fieldAliasRows);
            
            if (duplicateAliases.length > 0) {
                alert(`Cannot save field aliases with duplicate alias names:\n${duplicateAliases.join(', ')}\n\nPlease ensure all alias names are unique.`);
                return;
            }
            
            const fieldAliasRequests: FieldAliasRequest[] = fieldAliasRows.map(row => ({
                field_alias_id: row.field_alias_id || undefined, // Keep existing ID for updates, undefined for new
                field_id: row.field_id || undefined,
                field_name: row.field_name,
                alias_name: row.alias_name,
                customer_detail_field_id: row.customer_detail_field_id || undefined,
                client_name: row.client_name,
                loan_type: loanType,
                field_label: row.field_label || row.alias_name,
                field_description: row.field_description,
                is_active: Boolean(row.is_active)
            }));

            const response = await apiClient.put('/api/v1/field_alias', fieldAliasRequests);
            if (response.status === 200) {
                alert('Field aliases created successfully');
                
                onSaveCallback(); // Notify parent if needed
                onModalClose();
            }
        } catch (error: any) {
            if (error.response) {
                alert('Error: ' + error.response.data.message);
            } else {
                alert('Error: ' + error.message);
            }
        }
    };
    return (
        <Modal open={open} onClose={onModalClose}>
            <Box sx={modalStyle}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography id="modal-title" variant="h6">
                        Configure Field Aliases - {loanType}
                    </Typography>
                    <IconButton onClick={onClose}>
                        <CloseIcon />
                    </IconButton>
                </Box>

                <Box mb={3}>
                    <Typography variant="body2" color="textSecondary">
                        Configure your field aliases by entering alias names and mapping them to existing standard fields or creating new custom fields.
                    </Typography>
                </Box>

                {fieldAliasRows.map((row, index) => {
                    // Render appropriate component based on field type
                    if (row.isMandatoryStandard) {
                        return (
                            <MandatoryFieldRow
                                key={row.id}
                                row={row}
                                index={index}
                                updateRow={updateRow}
                                undoDelete={undoDelete}
                                isDuplicateAliasName={isDuplicateAliasNameForComponent}
                            />
                        );
                    } else if (row.customer_detail_field_id !== null && row.customer_detail_field_id !== undefined) {
                        // Only switch to CustomerReferenceFieldRow if customer_detail_field_id is explicitly set
                        // This happens when user selects "Customer Reference" option
                        return (
                            <CustomerReferenceFieldRow
                                key={row.id}
                                row={row}
                                index={index}
                                updateRow={updateRow}
                                removeRow={removeRow}
                                undoDelete={undoDelete}
                                isDuplicateAliasName={isDuplicateAliasNameForComponent}
                                customerDetailFields={customerDetailFields}
                                customerDetailAttributeFields={customerDetailAttributeFields}
                                handleCustomerDetailTypeSelection={handleCustomerDetailTypeSelection}
                                handleCustomerDetailNumberSelection={handleCustomerDetailNumberSelection}
                                handleCustomerDetailAttributeSelection={handleCustomerDetailAttributeSelection}
                                getCustomerDetailNumbers={getCustomerDetailNumbers}
                                handleFieldSelection={handleFieldSelection}
                                secondaryFields={secondaryFields}
                            />
                        );
                    } else {
                        return (
                            <CustomFieldRow
                                key={row.id}
                                row={row}
                                index={index}
                                updateRow={updateRow}
                                removeRow={removeRow}
                                undoDelete={undoDelete}
                                isDuplicateAliasName={isDuplicateAliasNameForComponent}
                                secondaryFields={secondaryFields}
                                handleFieldSelection={handleFieldSelection}
                            />
                        );
                    }
                })}

                <Box mt={2} display="flex" justifyContent="center" gap={2}>
                    <Button
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={addNewRow}
                        sx={{ minWidth: '200px' }}
                    >
                        Add Field Alias
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={<UploadFileIcon />}
                        component="label"
                        sx={{ minWidth: '200px' }}
                        color="secondary"
                    >
                        Upload Excel File (Sheet1)
                        <input
                            type="file"
                            accept=".xlsx,.xls"
                            hidden
                            onChange={handleFileUpload}
                        />
                    </Button>
                </Box>

                <Box mt={3} display="flex" justifyContent="flex-end" gap={2}>
                    <Button variant="outlined" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={saveFieldAliases}
                        disabled={
                            hasDuplicateAliases(fieldAliasRows) ||
                            fieldAliasRows.some(row => 
                                row.is_active && (
                                    !row.alias_name.trim() ||
                                    (!row.field_id && !row.customer_detail_field_id) ||
                                    ((row.customer_detail_field_id || row.field_type === 'CUSTOMER_DETAIL_ATTRIBUTE') && (!row.customerDetailType || !row.customerDetailAttribute || !row.field_name))
                                )
                            )
                        }
                    >
                        Save Field Aliases
                    </Button>
                </Box>

            </Box>
        </Modal>
    );
};

export default ConfigureFieldAliasModal;
